import { useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { get, keyBy, merge, values } from 'lodash';
import QueryString from 'qs';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { GetDashboardPreference_vm_profile_dashboardPreference_dashboardComponents_widget_VOLUNTEER_ChartWidgetType as IWidget } from '../../dashboard-widget-setting/__generated__/GetDashboardPreference';
import { DimensionEnum, valueMap } from '../../dashboard-widget-setting/dashboard-widget-model';
import { isFieldValueTarget } from '../../dashboard-widget-setting/useFieldValueFields';
import { WidgetDisplayContext } from '../../WidgetDisplayContext';
import { getEndDateFromRange, getStartDateFromRange } from '../shared';

type ITarget = {
  name: string;
  tag: string;
};

export const useQueryChart = (widget: IWidget) => {
  const {
    filters,
    targets,
    configuration: { chartType, dimension, interval, range, group },
  } = widget;

  const baseUrl = useApiUrl();
  const query = useCampfireFetch({ defer: true });
  const [data, setData] = useState<any>();

  const startDate = useMemo(
    () =>
      getStartDateFromRange(range) ??
      DateTime.local()
        .minus({ years: 10 })
        .startOf('day')
        .toISODate(),
    [range]
  );
  const endDate = useMemo(() => getEndDateFromRange(range) ?? DateTime.local().toISODate(), [range]);

  const { reportFields } = useContext(WidgetDisplayContext);

  useEffect(() => {
    let cancelled = false;
    const parsedTargets = JSON.parse(targets) as ITarget[];
    const parsedFilters = JSON.parse(filters);
    const grouped = dimension === DimensionEnum.BY_TIME && group !== null;

    const seriesName = ({ name, tag }: ITarget) =>
      isFieldValueTarget(tag.toLowerCase(), name.toLowerCase())
        ? get(reportFields, [get(parsedFilters, ['fieldIds', 0])], '')
        : get(valueMap, [tag.toLowerCase(), name.toLowerCase(), 'displayName'], name);

    const fetchTarget = async ({ name, tag }: ITarget) => {
      try {
        const response = await query.run({
          url: `${baseUrl}/vm/dashboard/management/${tag.toLowerCase()}/${name.toLowerCase()}/chart`,
          params: {
            interval,
            dimension,
            startDate,
            endDate,
            group,
            filters: parsedFilters,
          },
          paramsSerializer: (params) => {
            return QueryString.stringify(params, { arrayFormat: 'brackets', encode: false, skipNulls: true });
          },
        });
        return get(response, 'data.data');
      } catch (e) {
        return undefined;
      }
    };

    const getData = async () => {
      const results = [];
      /* eslint-disable no-await-in-loop */
      for (let i = 0; i < parsedTargets.length; i++) {
        results.push(await fetchTarget(parsedTargets[i]));
      }
      /* eslint-enable no-await-in-loop */

      if (grouped) {
        const rows: { [date: string]: { [key: string]: string | number } } = {};
        results.forEach((result, index) => {
          const prefix = parsedTargets.length > 1 ? `${seriesName(parsedTargets[index])}: ` : '';
          ((result?.data as any[]) || []).forEach(({ date, ...entities }) => {
            rows[date] = rows[date] || { date };
            Object.entries(entities).forEach(([entity, value]) => {
              rows[date][`${prefix}${entity}`] = value as number;
            });
          });
        });
        return { data: values(rows), entities: [] };
      }

      const key = dimension === DimensionEnum.BY_TIME ? 'date' : 'name';
      let mergedData = {};
      results.forEach((result, index) => {
        mergedData = merge(
          mergedData,
          keyBy(
            ((result as any[]) || []).map((datum) => ({
              [seriesName(parsedTargets[index])]: datum.total,
              [key]: datum[key],
            })),
            key
          )
        );
      });
      return values(mergedData);
    };

    getData().then((result) => {
      if (!cancelled) {
        setData(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dimension, interval, startDate, endDate, chartType, targets, group, filters, reportFields]);

  return data;
};
